/**
 * /documentos e /documentos/$id — G7 §13.
 *
 * A listagem junta NF-e e NFS-e porque a pergunta "quais documentos fiscais
 * esta empresa tem" não distingue modelo. O detalhe separa, porque os dois têm
 * identificadores diferentes e fingir que são o mesmo campo foi um dos erros
 * que este projeto herdou.
 *
 * O DETALHE MOSTRA O QUE EXISTE, E DIZ O QUE NÃO EXISTE
 *
 *   O §83 pede "quando disponível". Um campo nulo aparece como `—`, e não
 *   desaparece da tela: sumir com a linha faria parecer que o campo não se
 *   aplica, quando o que houve foi ausência de dado.
 *
 *   A diferença importa numa NFS-e em `awaiting_distribution`: ela tem número da
 *   prefeitura e NÃO tem nDPS nem chave de acesso. Ver os dois campos vazios é a
 *   informação — é o que "aguardando distribuição" significa na prática.
 */

import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { useSession } from '../lib/session.js';
import {
  fetchArtifacts,
  fetchNfeDocuments,
  fetchNfseDocuments,
  type ArtifactRow,
} from '../lib/queries.js';
import { AsyncPanel, ErrorState, LoadingState, ausente, dataBr } from '../components/States.js';
import { DocumentStatusBadge } from '../components/DocumentStatusBadge.js';
import { EnvironmentCell } from '../components/EnvironmentCell.js';

interface LinhaUnificada {
  readonly id: string;
  readonly familia: 'NF-e' | 'NFS-e';
  readonly environment: string;
  readonly serie: string | null;
  readonly numero: string | null;
  readonly status: string;
  readonly created_at: string;
}

export function DocumentosPage() {
  const { selected } = useSession();
  const id = selected?.id ?? '';

  const nfse = useQuery({
    queryKey: ['nfse', id],
    queryFn: () => fetchNfseDocuments(id),
    enabled: id !== '',
  });
  const nfe = useQuery({
    queryKey: ['nfe', id],
    queryFn: () => fetchNfeDocuments(id),
    enabled: id !== '',
  });

  const query = {
    isPending: nfse.isPending || nfe.isPending,
    isError: nfse.isError || nfe.isError,
    error: nfse.error ?? nfe.error,
    data:
      nfse.data === undefined || nfe.data === undefined
        ? undefined
        : ([
            ...nfse.data.map(
              (d): LinhaUnificada => ({
                id: d.id,
                familia: 'NFS-e',
                environment: d.environment,
                serie: d.serie,
                /* O número que a PREFEITURA deu, não o RPS. */
                numero: d.provider_document_ref ?? d.nfse_number,
                status: d.status,
                created_at: d.created_at,
              }),
            ),
            ...nfe.data.map(
              (d): LinhaUnificada => ({
                id: d.id,
                familia: 'NF-e',
                environment: d.environment,
                serie: d.serie,
                numero: d.number,
                status: d.status,
                created_at: d.created_at,
              }),
            ),
          ].sort((a, b) => b.created_at.localeCompare(a.created_at)) as LinhaUnificada[]),
  };

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">Documentos</h1>
        <p className="of-page__subtitle">Todos os documentos fiscais desta identidade.</p>
      </header>

      <AsyncPanel
        query={query}
        title="Documentos"
        emptyTitle="Nenhum documento fiscal"
        emptyDetail="Nada foi emitido nem custodiado para esta identidade fiscal."
      >
        {(rows) => (
          <div className="of-table-wrap">
            <table className="of-table">
              <thead>
                <tr>
                  <th>Família</th>
                  <th>Status</th>
                  <th>Amb.</th>
                  <th>Série</th>
                  <th>Número</th>
                  <th>Criado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((d) => (
                  <tr key={`${d.familia}-${d.id}`}>
                    <td>
                      <span className="of-tag">{d.familia}</span>
                    </td>
                    <td>
                      <DocumentStatusBadge status={d.status} />
                    </td>
                    <td>
                      <EnvironmentCell value={d.environment} />
                    </td>
                    <td className="of-mono">{ausente(d.serie)}</td>
                    <td className="of-mono">{ausente(d.numero)}</td>
                    <td>{dataBr(d.created_at)}</td>
                    <td>
                      {d.familia === 'NFS-e' && (
                        <Link to="/documentos/$id" params={{ id: d.id }} className="of-link">
                          detalhe
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </AsyncPanel>
    </section>
  );
}

function Campo({ rotulo, valor, nota }: { rotulo: string; valor: string; nota?: string }) {
  return (
    <div className="of-field-row">
      <dt className="of-field-row__label">{rotulo}</dt>
      <dd className="of-field-row__value">
        <span className="of-mono">{valor}</span>
        {nota !== undefined && <span className="of-field-row__note">{nota}</span>}
      </dd>
    </div>
  );
}

/** O que cada tipo de artefato é, em uma linha. */
const ARTEFATO: Readonly<Record<string, string>> = Object.freeze({
  official_nfse: 'documento fiscal oficial, assinado pelo Município',
  request_payload: 'o pedido exato que saiu daqui',
  provider_response: 'retorno proprietário do canal — não é documento fiscal',
  cancellation_request: 'o pedido de cancelamento que saiu daqui',
  cancellation_provider_response: 'retorno do canal ao cancelamento',
  cancelled_danfse_pdf: 'DANFSe da nota cancelada, do Município',
});

export function DocumentoDetalhePage() {
  const { id: documentoId } = useParams({ from: '/documentos/$id' });
  const { selected } = useSession();
  const identityId = selected?.id ?? '';

  const nfse = useQuery({
    queryKey: ['nfse', identityId],
    queryFn: () => fetchNfseDocuments(identityId),
    enabled: identityId !== '',
  });
  const artefatos = useQuery({
    queryKey: ['artefatos', identityId],
    queryFn: () => fetchArtifacts(identityId),
    enabled: identityId !== '',
  });

  if (nfse.isPending || artefatos.isPending) return <LoadingState />;
  if (nfse.isError) return <ErrorState title="Documento" error={nfse.error} />;

  const doc = (nfse.data ?? []).find((d) => d.id === documentoId);

  /*
   * Documento não encontrado NÃO é "não autorizado", e vice-versa.
   *
   * A RLS não devolve linhas de outra identidade — então um id válido de outra
   * empresa chega aqui como ausência. Dizer "não encontrado" é o que se sabe;
   * dizer "acesso negado" confirmaria que o documento existe, que é informação
   * que a política acabou de recusar.
   */
  if (doc === undefined) {
    return (
      <section className="of-page">
        <div className="of-panel-state of-panel-state--empty" role="alert">
          <strong className="of-panel-state__title">Documento não encontrado</strong>
          <p className="of-panel-state__detail">
            Nenhum documento com este identificador é visível nesta identidade fiscal.
          </p>
        </div>
      </section>
    );
  }

  const doDocumento: ArtifactRow[] = (artefatos.data ?? []).filter(
    (a) => a.nfse_document_id === doc.id,
  );

  return (
    <section className="of-page">
      <header className="of-page__header">
        <h1 className="of-page__title">
          NFS-e {ausente(doc.provider_document_ref ?? doc.nfse_number)}
        </h1>
        <div className="of-page__badges">
          <DocumentStatusBadge status={doc.status} code={doc.rejection_code} />
          <EnvironmentCell value={doc.environment} />
        </div>
      </header>

      <dl className="of-fields">
        <Campo rotulo="Identidade fiscal" valor={`${selected?.legal_name} · ${selected?.tax_id}`} />
        <Campo rotulo="Provedor" valor={ausente(doc.provider_id)} />
        <Campo rotulo="Município emissor" valor={ausente(doc.issuer_municipality_code)} />
        <Campo rotulo="Série" valor={ausente(doc.serie)} />
        <Campo
          rotulo="RPS"
          valor={ausente(doc.provider_rps_number)}
          nota="numeração nossa, alocada antes de transmitir"
        />
        <Campo
          rotulo="Número da NFS-e"
          valor={ausente(doc.provider_document_ref ?? doc.nfse_number)}
          nota="atribuído pela prefeitura"
        />
        <Campo
          rotulo="nDPS"
          valor={ausente(doc.numero)}
          nota="identificador no Padrão Nacional; chega pela distribuição"
        />
        <Campo rotulo="Código de verificação" valor={ausente(doc.provider_protocol)} />
        <Campo rotulo="Chave de acesso" valor={ausente(doc.access_key)} />
        <Campo rotulo="DPS" valor={ausente(doc.dps_id)} />
        <Campo rotulo="Autorizada em" valor={dataBr(doc.authorized_at)} />
        <Campo rotulo="Cancelada em" valor={dataBr(doc.cancelled_at)} />
        <Campo rotulo="Criado em" valor={dataBr(doc.created_at)} />
        {doc.rejection_message !== null && (
          <Campo rotulo="Motivo da rejeição" valor={doc.rejection_message} />
        )}
      </dl>

      <h2 className="of-section-title">Artefatos custodiados</h2>
      {doDocumento.length === 0 ? (
        <div className="of-panel-state of-panel-state--empty">
          <strong className="of-panel-state__title">Nenhum artefato</strong>
          <p className="of-panel-state__detail">
            Nada foi custodiado para este documento.
          </p>
        </div>
      ) : (
        <div className="of-table-wrap">
          <table className="of-table">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Fonte</th>
                <th>Entrada</th>
                <th>Bytes</th>
                <th>SHA-256</th>
                <th>Custodiado</th>
              </tr>
            </thead>
            <tbody>
              {doDocumento.map((a) => (
                <tr key={a.id}>
                  <td>
                    <span className="of-mono">{a.kind}</span>
                    {ARTEFATO[a.kind] !== undefined && (
                      <span className="of-cell__hint"> {ARTEFATO[a.kind]}</span>
                    )}
                  </td>
                  <td className="of-mono">{a.source}</td>
                  <td className="of-mono">{ausente(a.ingestion_origin)}</td>
                  <td className="of-mono">{ausente(a.byte_size)}</td>
                  {/* O hash inteiro. É ele que prova que o arquivo é o mesmo. */}
                  <td className="of-mono of-cell--key">{a.sha256}</td>
                  <td>{dataBr(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="of-page__footnote">
        O console mostra que o artefato existe, seu tamanho e seu hash. O caminho no cofre não
        chega ao browser — o download, quando existir, sai por URL assinada gerada no servidor.
      </p>
    </section>
  );
}
